import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore.ts";
import { logout } from "../api/auth.ts";
import VersionBadge from "./VersionBadge.tsx";
import {
  LayoutDashboard,
  ClipboardList,
  Upload,
  Settings,
  Globe,
  LogOut,
  ShieldCheck,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import DataNoticeModal from "./DataNoticeModal.tsx";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/screening", icon: ClipboardList, label: "Screening", roles: ["ADMIN", "COORDINATOR", "SUB_TEAM_LEADER", "REVIEWER"] },
  { to: "/import", icon: Upload, label: "Import", roles: ["ADMIN", "COORDINATOR"] },
  { to: "/admin", icon: Settings, label: "Admin", roles: ["ADMIN", "COORDINATOR", "SUB_TEAM_LEADER"] },
  { to: "/public", icon: Globe, label: "Public View" },
];

// Per-browser preference only, so storage failures just mean "expanded".
const COLLAPSE_KEY = "sidebarCollapsed";
function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
}

export default function Layout() {
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [showNotice, setShowNotice] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    navigate("/login");
  };

  const visibleNav = navItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  );

  // Collapsed: icon-only rows, centred, with the label kept as a tooltip.
  const rowLayout = collapsed ? "justify-center px-0" : "gap-3 px-3";

  return (
    <div className="flex h-screen bg-background">
      {showNotice && <DataNoticeModal mode="view" onClose={() => setShowNotice(false)} />}

      {/* Sidebar */}
      <aside className={`${collapsed ? "w-16" : "w-64"} shrink-0 border-r bg-card flex flex-col transition-[width] duration-200`}>
        <div className={`border-b flex items-center ${collapsed ? "justify-center p-3" : "justify-between p-4"}`}>
          {!collapsed && (
            <div className="min-w-0">
              <img src="/neuroxrehab-logo.svg" alt="neuroXRehab" className="h-8 w-auto" />
              <p className="text-xs text-muted-foreground mt-1">Living Review Platform</p>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors shrink-0"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        <nav className={`flex-1 space-y-1 ${collapsed ? "p-2" : "p-3"}`}>
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              aria-label={item.label}
              className={({ isActive }) =>
                `flex items-center ${rowLayout} py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-brand-magenta/10 text-brand-magenta font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && item.label}
            </NavLink>
          ))}
        </nav>

        <div className={`border-t ${collapsed ? "p-2 space-y-1" : "p-3"}`}>
          {collapsed ? (
            <div
              className="mx-auto h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium"
              title={`${user?.name ?? ""} (${user?.role ?? ""})`}
            >
              {(user?.name ?? "?").trim().charAt(0).toUpperCase()}
            </div>
          ) : (
            <div className="px-3 py-2 mb-1">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
            </div>
          )}
          <VersionBadge compact={collapsed} />
          <button
            onClick={handleLogout}
            title={collapsed ? "Sign out" : undefined}
            aria-label="Sign out"
            className={`flex items-center ${rowLayout} w-full py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors`}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && "Sign out"}
          </button>
          <button
            onClick={() => setShowNotice(true)}
            title={collapsed ? "Data & privacy notice" : undefined}
            aria-label="Data & privacy notice"
            className={`flex items-center ${collapsed ? "justify-center px-0" : "gap-2 px-3"} w-full py-1.5 mt-1 rounded-md text-xs text-muted-foreground/60 hover:text-muted-foreground hover:bg-accent/50 transition-colors`}
          >
            <ShieldCheck className="h-3 w-3 shrink-0" />
            {!collapsed && <>Data &amp; privacy notice</>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
